/**
 * How the review dialog words a save or delete that failed.
 *
 * The dialog (src/components/product/review-dialog.tsx) is the one place that
 * toasts for both kinds of review: an account review is written straight to
 * Firestore, a guest's goes through the submitGuestReview callable and
 * /api/download/delete-guest-review. The handlers on the product page report a
 * failure by throwing, and this turns whatever they threw into one sentence the
 * buyer can act on, without ever showing raw SDK or runtime error text.
 */

/** A failure whose message is already written for the buyer. */
export class ReviewActionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ReviewActionError';
  }
}

export type ReviewAction = 'save' | 'delete';

export const REVIEW_OFFLINE_MESSAGE = "We couldn't reach supa.place. Check your connection and try again.";
export const REVIEW_PERMISSION_MESSAGE = "You don't have permission to change this review.";
/** For a page state that should not be reachable, such as no product or no Firestore. */
export const REVIEW_REFRESH_MESSAGE = 'Something went wrong on this page. Refresh it and try again.';
/**
 * submitGuestReview is create-only (see the comment there: an overwrite keyed
 * on values the seller can read let a seller replace a buyer's review), so a
 * guest's "edit" always ends here. Deleting is the way to a new one.
 */
export const GUEST_REVIEW_EXISTS_MESSAGE =
  "You've already reviewed this order, and a guest review can't be edited. To change it, delete it and write a new one.";

const FALLBACK: Record<ReviewAction, string> = {
  save: 'Your review is still here. Try again in a moment.',
  delete: 'Your review is still up. Try again in a moment.',
};

export function reviewActionFallback(action: ReviewAction): string {
  return FALLBACK[action];
}

function stringField(err: unknown, key: 'code' | 'message'): string {
  if (!err || typeof err !== 'object') return '';
  const value = (err as Record<string, unknown>)[key];
  return typeof value === 'string' ? value.trim() : '';
}

/** The sentence to show for a failed review `action`. */
export function reviewActionErrorMessage(err: unknown, action: ReviewAction): string {
  if (err instanceof ReviewActionError) return err.message;
  const code = stringField(err, 'code');
  // Callable (HttpsError) failures. The server writes these for the buyer
  // ("Refunded purchases can't be reviewed."), except internal and unknown,
  // which carry only the word "internal".
  if (code.startsWith('functions/')) {
    const fnCode = code.slice('functions/'.length);
    if (fnCode === 'already-exists') return GUEST_REVIEW_EXISTS_MESSAGE;
    if (fnCode === 'unavailable' || fnCode === 'deadline-exceeded') return REVIEW_OFFLINE_MESSAGE;
    const message = stringField(err, 'message');
    if (fnCode === 'internal' || fnCode === 'unknown' || !message) return FALLBACK[action];
    return message;
  }
  // Firestore failures, whose messages are the SDK's ("Missing or insufficient
  // permissions."), not copy.
  if (code === 'permission-denied') return REVIEW_PERMISSION_MESSAGE;
  if (code === 'unavailable' || code === 'deadline-exceeded') return REVIEW_OFFLINE_MESSAGE;
  return FALLBACK[action];
}
